import {MediaBrowserPanelTrackingContextState} from '../MediaBrowserPanelTrackingContext';

/**
 * Represents a single stage in the media loading pipeline.
 */
export type TrackingStage = {
  /** Relative start time in milliseconds from panel creation */
  startTime: number;
  /** Duration of this stage in milliseconds */
  duration: number;
};

/**
 * All stages in the media tile loading pipeline.
 *
 * Each stage is tracked independently to identify performance bottlenecks.
 * See MEDIA_TRACKING_DATADOG.md for detailed documentation.
 */
export type TrackingStages = {
  /** Component setup and initialization (panel → component mount) */
  processing: TrackingStage;
  /** Time to receive available file metadata from API */
  files: TrackingStage;
  /** Time to resolve direct media URLs (CDN, signed URLs) */
  urls: TrackingStage;
  /** Network download of media blob (can be slow for large files) */
  blobFetch: TrackingStage;
  /** Memory operations to cache blob (object URL creation) */
  cacheUpdate: TrackingStage;
  /** Time to load and render the actual media asset */
  load: TrackingStage;
};

export type TrackingState = {
  componentCreatedPerformanceMs: number;
  availableFilesReceivedPerformanceMs: number;
  directUrlReceivedPerformanceMs: number;
  blobFetchCompletePerformanceMs: number;
  dataCachedPerformanceMs: number;
  mediaLoadedPerformanceMs: number;
  panel: MediaBrowserPanelTrackingContextState;
};

/**
 * Calculates duration and timing for each stage of media tile loading.
 *
 * This function breaks down the media loading pipeline into discrete stages,
 * calculating both the duration of each stage and its start time relative to
 * the panel creation. These stages are then sent to Datadog as separate vitals
 * for independent analysis.
 *
 * Pipeline flow:
 * 1. processing:   Panel created → Component mounted
 * 2. files:        Component mounted → Files metadata received
 * 3. urls:         Files received → Direct URLs resolved
 * 4. blobFetch:    URLs resolved → Blob downloaded (network)
 * 5. cacheUpdate:  Blob ready → Data cached (memory)
 * 6. load:         Data cached → Media loaded and rendered
 *
 * @param state - Current tracking state with all timestamp measurements
 * @returns TrackingStages object with timing for each stage, or null if incomplete
 *
 * @example
 * const stages = calculateTrackingStages(trackingState);
 * if (stages) {
 *   console.log(`Blob fetch took ${stages.blobFetch.duration}ms`);
 *   console.log(`Cache update took ${stages.cacheUpdate.duration}ms`);
 * }
 */
export function calculateTrackingStages(
  state: TrackingState
): TrackingStages | null {
  if (!state.mediaLoadedPerformanceMs || !state.panel?.startTimePerformanceMs) {
    return null;
  }

  const panelStartTimePerformance = state.panel.startTimePerformanceMs;

  // Calculate relative start times (when each stage begins, relative to panel start)
  const tileRelativeStartTimeFiles =
    state.componentCreatedPerformanceMs - panelStartTimePerformance;
  const tileRelativeStartTimeUrls =
    state.availableFilesReceivedPerformanceMs - panelStartTimePerformance;
  const tileRelativeStartTimeBlobFetch =
    state.directUrlReceivedPerformanceMs - panelStartTimePerformance;
  const tileRelativeStartTimeCacheUpdate =
    state.blobFetchCompletePerformanceMs - panelStartTimePerformance;
  const tileRelativeStartTimeLoad =
    state.dataCachedPerformanceMs - panelStartTimePerformance;

  // Calculate stage durations (how long each stage takes)
  const tileDurationProcessing =
    state.componentCreatedPerformanceMs - panelStartTimePerformance;
  const tileDurationFiles =
    state.availableFilesReceivedPerformanceMs -
    state.componentCreatedPerformanceMs;
  const tileDurationUrls =
    state.directUrlReceivedPerformanceMs -
    state.availableFilesReceivedPerformanceMs;
  const tileDurationBlobFetch =
    state.blobFetchCompletePerformanceMs - state.directUrlReceivedPerformanceMs;
  const tileDurationCacheUpdate =
    state.dataCachedPerformanceMs - state.blobFetchCompletePerformanceMs;
  const tileDurationLoad =
    state.mediaLoadedPerformanceMs - state.dataCachedPerformanceMs;

  return {
    // NOTE: this lumps a good bit of work into "processing"
    // TODO: add more granular stages for processing to identify where we spend time
    processing: {
      startTime: 0, // Always starts at panel creation
      duration: tileDurationProcessing,
    },
    files: {
      startTime: tileRelativeStartTimeFiles,
      duration: tileDurationFiles,
    },
    urls: {
      startTime: tileRelativeStartTimeUrls,
      duration: tileDurationUrls,
    },
    blobFetch: {
      startTime: tileRelativeStartTimeBlobFetch,
      duration: tileDurationBlobFetch,
    },
    cacheUpdate: {
      startTime: tileRelativeStartTimeCacheUpdate,
      duration: tileDurationCacheUpdate,
    },
    load: {
      startTime: tileRelativeStartTimeLoad,
      duration: tileDurationLoad,
    },
  };
}

/**
 * Calculates the total end-to-end duration for media tile loading.
 *
 * This is the user-perceived load time from when the panel was created
 * to when the media finished loading and rendering.
 *
 * @param state - Current tracking state with all timestamp measurements
 * @returns Total duration in milliseconds, or null if loading not yet complete
 */
export function calculateTotalDuration(state: TrackingState): number | null {
  if (!state.mediaLoadedPerformanceMs || !state.panel?.startTimePerformanceMs) {
    return null;
  }
  return state.mediaLoadedPerformanceMs - state.panel.startTimePerformanceMs;
}
